import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { StressResultsService } from '../../services/stress-results.service';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { lastValueFrom } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { SingleLoadResultsUi } from '../../models/perivis-results-state.model';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';

@Component({
  selector: 'app-single-load-axial',
  templateUrl: './single-load-axial.component.html',
  styles: [],
  standalone: false
})
export class SingleLoadAxialComponent implements OnInit, OnDestroy {
  private _forceUnit: string;
  private selectedString: string;

  public axialResults: any[];
  public loadCases: any[] = [];
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public modes: SelectItem[] = [
    { label: 'Single', value: 'single' },
    { label: 'Multiple', value: 'multiple' }
  ];
  public errorMsg: string;
  public results: any[];
  public depthView: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public isLoading = signal<boolean>(true);
  public selectedMode: string = 'single';
  public selectedResultDisplay: string = 'plot';
  public selectedDepthView: string = 'md';
  public yAxisTitle = '';
  public xAxisTitle = '';
  public tableName: string;
  public userUnits: UserUnitsModel;
  public plotName: string;
  public signalRChange: boolean;
  public longLengthUnit: string;

  public columnDefs: { header: string, field: string }[];
  public columnDefinitions: any[];

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.axialResultsStore = this._storeService.createStore(this.componentId, new SingleLoadResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedMode = this.axialResultsStore.state.selectedMode || 'single';
    this.selectedResultDisplay = this.axialResultsStore.state.selectedResultDisplay || 'plot';
    this.selectedDepthView = this.axialResultsStore.state.selectedDepthView || 'md';
  }
  get componentId(): string {
    return this._componentId;
  }
  public axialResultsStore: Store;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.longLengthUnit = this.userUnits.longLengths;
    this._forceUnit = this.userUnits?.force;
    this.xAxisTitle = `Axial Force (${this._forceUnit})`;
    this.yAxisTitle = `MD (${this.longLengthUnit})`;

    // this.refreshResults(true, false);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.refreshResults(true, false, false);

    this.columnDefs = [
      { header: 'Axial Load', field: 'axialLoad' },
      { header: 'Axial Rating', field: 'axialRating' },
    ];
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Axial Load (${this._forceUnit})`, field: 'axialLoad',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Axial Rating (${this._forceUnit})`, field: 'axialRating',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.refreshResults(true, true, false);
    }
  }

  public async refreshResults(isLoading: boolean, calledFromSignalR: boolean, calledFromMode?: boolean): Promise<void> {
    this.isLoading.set(isLoading);

    this.xAxisTitle = `Axial Force (${this._forceUnit})`;

    if ((!this.axialResults || calledFromSignalR) && !calledFromMode) {
      try {
        this.axialResults = await lastValueFrom(this._stressResultsService.getAxialResults());
        this.signalRChange = true;
      } catch {
        this.errorMsg = "Well Configuration Missing: Please specify at least one string";
        this.isLoading.set(false);
        return;
      }
    }

    if (this.axialResults && (this.axialResults.length === 0 || this.axialResults[0].loadCaseName === '')) {
      this.errorMsg = "Please calculate PERIVIS to see results";
      this.columnDefs = [];
      this.results = [];
      this.isLoading.set(false);
      return;
    } else {
      this.errorMsg = null;
    }

    if (this.axialResultsStore.state.selectedMode == "single") {
      this.handleSingleAxialLoad(this.axialResults);
    } else {
      this.handleMultiAxialLoad(this.axialResults)
    }

    this.isLoading.set(false);
  }

  onModeChange(e): void {
    this.selectedMode = e.value;
    this.axialResultsStore.update(state => ({ ...state, selectedMode: e.value }));
    if (this.axialResultsStore.state.selectedMode == "multiple") {
      this.axialResultsStore.update(state => ({ ...state, selectedLoadCase: { label: 'Axial Load', value: 'axialLoad' } }));
    } else {
      // this.xAxisTitle = `Axial Force (lbf)`;
    }
    this.plotName = this.setPlotName();
    this.refreshResults(true, false, true);
  }

  onSelectedResultDisplayUpdated(e): void {
    this.selectedResultDisplay = e.value;
    this.axialResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
  }

  public setLoadCase(e): void {
    if (this.axialResultsStore.state.selectedMode === "single") {
      this.axialResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.results = e.value.value.axialResults;
    } else {
      this.axialResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.refreshResults(true, false, false);
    }
  }

  setPlotName(): string {
    return 'axialForcesPlot - ' + this.selectedString + ' - ' + this.axialResultsStore.state.selectedMode + ' ' + this.axialResultsStore.state.selectedLoadCase.label;
  }

  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.axialResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  private handleSingleAxialLoad(axialResults: any[]) {

    this.columnDefs = [
      { header: 'Axial Load', field: 'axialLoad' },
      { header: 'Axial Rating', field: 'axialRating' },
    ];
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Axial Load (${this._forceUnit})`, field: 'axialLoad',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Axial Rating (${this._forceUnit})`, field: 'axialRating',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];
    this.results = [];

    this.loadCases = axialResults?.map(lc => {
      return { label: lc.loadCaseName, value: lc };
    }) ?? [];
    if (!this.axialResultsStore.state.selectedLoadCase || !this.loadCases.find(l => l.value.tubularStringId == this.axialResultsStore.state.selectedLoadCase?.value.tubularStringId)) {
      this.axialResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      this.plotName = this.setPlotName();
    }
    this.plotName = this.setPlotName();
    this.results = this.axialResultsStore.state.selectedLoadCase?.value.axialResults || [];
  }

  private handleMultiAxialLoad(axialResults: any[]) {
    this.columnDefinitions.splice(2, this.columnDefinitions.length);
    this.columnDefs = [];
    this.results = [];

    this.loadCases = [
      { label: 'Axial Load', value: 'axialLoad' },
      { label: 'Axial Rating', value: 'axialRating' }
    ]

    this.xAxisTitle = this.axialResultsStore.state.selectedLoadCase.value === 'axialLoad' ? `Axial Load (${this._forceUnit})` : `Axial Rating (${this._forceUnit})`;

    axialResults.forEach(load => {
      const loadName: string = load.loadCaseName + ` (${this._forceUnit})`
      const loadNameCamel = toCamelCaseString(load.loadCaseName);

      this.columnDefs.push({ header: load.loadCaseName, field: loadNameCamel });

      this.columnDefinitions.push({
        header: loadName,
        field: loadNameCamel,
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      });

      const backupArray = [];

      load.axialResults.forEach(loadResult => {
        const result = {
          [loadNameCamel]: loadResult[this.axialResultsStore.state.selectedLoadCase.value],
          measuredDepth: loadResult.measuredDepth,
          trueVerticalDepth: loadResult.trueVerticalDepth
        }

        if (axialResults[0].loadCaseName === load.loadCaseName) {
          this.results.push(result);
        } else {
          backupArray.push(result);
        }
      });

      this.results = this.results.map(x => {
        const otherLoad = backupArray.find(e => e.measuredDepth === x.measuredDepth)
        return { ...x, ...otherLoad }
      });
    });
    this.plotName = this.setPlotName();
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
